import { motion } from "framer-motion";
import { Camera, Repeat, Film, Video } from "lucide-react";
import { Reveal, Chapter } from "./Reveal";
import { isVideo } from "../lib/site";
import { useContent } from "../lib/content";

const LAYOUT = [
  { icon: Camera, span: "md:col-span-2", h: "h-72 md:h-80" },
  { icon: Repeat, span: "", h: "h-72 md:h-80" },
  { icon: Film, span: "", h: "h-64" },
  { icon: Video, span: "md:col-span-2", h: "h-64" },
];

const BentoCard = ({ card, index }) => {
  const Icon = card.icon;
  return (
    <Reveal delay={index * 0.08} className={card.span}>
      <motion.div
        whileHover={{ scale: 1.02, y: -4 }}
        transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
        className={`group relative overflow-hidden rounded-3xl ${card.h} w-full shadow-[0_8px_30px_rgba(145,0,141,0.08)] cursor-pointer`}
        data-testid={`experience-card-${card.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
      >
        {isVideo(card.img) ? (
          <video
            src={card.img}
            muted
            loop
            autoPlay
            playsInline
            className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
          />
        ) : (
          <img
            src={card.img}
            alt={card.title}
            className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
          />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-[#3c003a]/90 via-[#3c003a]/30 to-transparent" />
        <div className="absolute bottom-0 left-0 right-0 p-6">
          <span className="inline-grid place-items-center w-10 h-10 rounded-xl bg-white/10 backdrop-blur-md border border-white/20 text-[#ffc4e4] mb-3">
            <Icon size={18} strokeWidth={1.8} />
          </span>
          <h3 className="font-serif text-white text-2xl mb-1">{card.title}</h3>
          <p className="text-white/70 text-sm leading-relaxed max-w-sm">{card.desc}</p>
        </div>
      </motion.div>
    </Reveal>
  );
};

export const Experience = () => {
  const { experience } = useContent();
  const cards = (experience || []).map((c, i) => ({ ...c, ...LAYOUT[i % LAYOUT.length] }));
  return (
  <section id="experience" className="py-24 lg:py-32 bg-[#fafaf7]" data-testid="experience-section">
    <div className="max-w-7xl mx-auto px-6 lg:px-10">
      <Chapter number="01" label="The GlamBooth Experience" />
      <div className="grid lg:grid-cols-2 gap-6 items-end mb-14">
        <Reveal>
          <h2 className="font-serif text-[#121212] text-3xl sm:text-4xl lg:text-5xl tracking-[-0.02em] leading-[1.1]">
            Every format. <em className="text-[#91008d]">Every</em>{" "}
            <em className="text-[#a3005a]">moment.</em>
          </h2>
        </Reveal>
        <Reveal delay={0.1}>
          <p className="text-[#121212]/60 text-base md:text-lg leading-[1.7] lg:max-w-md lg:ml-auto">
            One booth, four ways to relive the night. Guests tap, pose, and share
            in seconds — no apps, no waiting.
          </p>
        </Reveal>
      </div>
      <div className="grid md:grid-cols-3 gap-5">
        {cards.map((card, i) => (
          <BentoCard key={card.title} card={card} index={i} />
        ))}
      </div>
    </div>
  </section>
  );
};
